import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { Card } from '../Card';
import { Game, Standard } from '../../data/types';
import PageContainer from '../layouts/PageContainer';

const TeacherGameLibraryScreen: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [gamesData, standardsData] = await Promise.all([
          dataService.getGames(),
          dataService.getStandards()
        ]);
        
        setGames(gamesData);
        setStandards(standardsData);
      } catch (error) {
        console.error('Error fetching game library data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter games based on criteria
  const filteredGames = games.filter(game => {
    // Filter by grade
    if (filterGrade !== 'all' && game.gradeLevel !== filterGrade) {
      return false;
    }
    
    // Filter by game type
    if (filterType !== 'all' && game.type !== filterType) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery && !game.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Get standard name by ID
  const getStandardName = (standardId: string) => {
    const standard = standards.find(s => s.id === standardId);
    return standard ? standard.code : 'Unknown';
  };

  // Get difficulty badge with appropriate color
  const getDifficultyBadge = (difficulty: string) => {
    let bgColor = '';
    let textColor = '';
    
    switch(difficulty) {
      case 'Easy':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Medium':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Hard':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }
    
    return (
      <span className={`${bgColor} ${textColor} px-2 py-1 rounded-full text-xs font-medium`}>
        {difficulty}
      </span>
    );
  };

  if (loading) {
    return (
      <PageContainer title="Game Library" userRole="Teacher">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Game Library" userRole="Teacher">
      
      {/* Filters and Search */}
      <div className="mb-8">
        <Card className="bg-white p-6 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="md:w-1/3">
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Grade Level
              </label>
              <select
                id="grade-filter"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                value={filterGrade}
                onChange={(e) => setFilterGrade(e.target.value)}
              >
                <option value="all">All Grades</option>
                <option value="K">Kindergarten</option>
                <option value="1">Grade 1</option>
                <option value="2">Grade 2</option>
                <option value="3">Grade 3</option>
                <option value="4">Grade 4</option>
                <option value="5">Grade 5</option>
                <option value="6">Grade 6</option>
                <option value="7">Grade 7</option>
                <option value="8">Grade 8</option>
              </select>
            </div>
            
            <div className="md:w-1/3">
              <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Game Type
              </label>
              <select
                id="type-filter"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="Arcade">Arcade</option>
                <option value="Puzzle">Puzzle</option>
                <option value="Quiz">Quiz</option>
                <option value="Sorting">Sorting</option>
                <option value="DragAndDropSorting">Drag & Drop</option>
              </select>
            </div>
            
            <div className="md:w-1/3">
              <label htmlFor="search-query" className="block text-sm font-medium text-gray-700 mb-1">
                Search Games
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="search-query"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 pl-10"
                  placeholder="Search by title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Games Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {filteredGames.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No games found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your filters to find games.</p>
          </div>
        ) : (
          filteredGames.map((game) => (
            <Card key={game.id} className="bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="relative aspect-video overflow-hidden rounded-t-lg">
                <img 
                  src={game.thumbnail} 
                  alt={game.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  {getDifficultyBadge(game.difficulty)}
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-gray-800 truncate">{game.title}</h3>
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Grade {game.gradeLevel}
                  </span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <span className="mr-2">Type:</span>
                  <span className="font-medium">{game.type}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600 mb-4">
                  <span className="mr-2">Primary Standard:</span>
                  <span className="font-medium">{getStandardName(game.primaryStandardId)}</span>
                </div>
                
                <div className="flex justify-between">
                  <button
                    onClick={() => setSelectedGame(game)}
                    className="text-primary hover:text-primary-dark font-medium text-sm"
                  >
                    View Details
                  </button>
                  
                  <button
                    className="bg-primary text-white py-1 px-3 rounded text-sm hover:bg-primary-dark transition-colors"
                  >
                    Preview Game
                  </button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
      
      {/* Game Details Modal */}
      {selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-800">{selectedGame.title}</h2>
                <button 
                  onClick={() => setSelectedGame(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                  <div className="aspect-video overflow-hidden rounded-lg mb-4">
                    <img 
                      src={selectedGame.thumbnail} 
                      alt={selectedGame.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Grade Level:</span>
                      <span className="font-medium text-gray-800">
                        {selectedGame.gradeLevel === 'K' ? 'Kindergarten' : `Grade ${selectedGame.gradeLevel}`}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Difficulty:</span>
                      <span>{getDifficultyBadge(selectedGame.difficulty)}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Game Type:</span>
                      <span className="font-medium text-gray-800">{selectedGame.type}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Theme:</span>
                      <span className="font-medium text-gray-800">{selectedGame.theme}</span>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Supported Standards</h3>
                  <div className="border border-gray-200 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                    {selectedGame.supportedStandards.length === 0 ? (
                      <p className="text-sm text-gray-500">No standards specified</p>
                    ) : (
                      <div className="space-y-1">
                        {selectedGame.supportedStandards.map((standardId) => (
                          <div key={standardId} className="flex items-center">
                            <span className="text-sm text-gray-700">
                              {getStandardName(standardId)}
                            </span>
                            {standardId === selectedGame.primaryStandardId && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                Primary
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-gray-700 mb-2">Game Preview</h3>
                  <div className="space-y-3">
                    <button className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Preview Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default TeacherGameLibraryScreen;
